import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { CandidatesByCandidateBatchItem } from '../types/CandidatesByCandidateBatch';
import {
  deleteSearchParams,
  updateSearchParams
} from 'services/navigation/paths';
import { TableV3, TableV3Ref } from 'common/table';
import { TableV3Filters } from 'common/table/components/TableV3';
import { TableV3Sort } from 'common/table/types/TableV3';
import { candidatesTableColumns } from '../tables/columns/candidatesTableColumns';
import { mapCandidatesDataToTableColumns } from '../tables/maps/mapCandidatesDataToTableColumns';
import { CandidatesTableColumns } from '../tables/types/CandidatesTable';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { useCandidateReportDrawerParams } from 'pages/CandidatesRanking/hooks/useCandidateReportDrawerParams';
import { useUpdateCandidateMutation } from 'generated-graphql/hooks/apollo-hooks';
import {
  BottomDrawerComponentType,
  useBottomDrawer
} from 'core/stores/bottomDrawer';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { EditCandidateDetails } from './EditCandidateDetails';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { SetURLSearchParams } from 'react-router-dom';
import { SearchableCandidateSortableFields } from 'generated-graphql/types';
import { ToastType, useToast } from 'services/toast';
import { IntegrationCredentials } from 'services/integration/types';

type Props = {
  candidates: CandidatesByCandidateBatchItem[];
  isLoading: boolean;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  paginationTotalRows: number;
  currentPage: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  onFiltersChanged: (filters: TableV3Filters<CandidatesTableColumns>) => void;
  onSort: (sort: TableV3Sort<SearchableCandidateSortableFields>) => void;
  sort: TableV3Sort<SearchableCandidateSortableFields>;
  isReadOnly: boolean;
  emptyStateComponent: React.ReactNode | undefined;
  integration: IntegrationCredentials | null;
};

export const CandidatesTable: React.FC<Props> = ({
  candidates,
  isLoading,
  setSearchParams,
  searchParams,
  paginationTotalRows,
  currentPage,
  onChangePage,
  onChangePageSize,
  onFiltersChanged,
  onSort,
  sort,
  isReadOnly,
  emptyStateComponent,
  integration
}) => {
  const { showToast } = useToast();

  useCandidateReportDrawerParams();
  const tableRef = useRef<TableV3Ref<CandidatesTableColumns>>(null);

  const { drawerContentType, openDrawer, updateDrawerComponentProps } =
    useDrawer();
  const { track } = useTracking();

  const { openDrawer: openDrawerV2 } = useDrawerV2();

  const {
    closeBottomDrawer,
    openBottomDrawer,
    isOpen,
    updateBottomDrawerComponentProps
  } = useBottomDrawer();

  const [updateCandidate] = useUpdateCandidateMutation({
    onCompleted: () => {
      // closeModal();
    }
  });

  const updateCandidateLockStatus = async (candidateId: string) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (!candidate) return;

    if (candidate.isLocked === false) {
      track(TrackingEventName.AnalyseLockCandidate, { isLocked: true });
    }

    await updateCandidate({
      variables: {
        input: {
          id: candidateId,
          isLocked: !candidate.isLocked
        }
      }
    });

    showToast({
      message: `${candidate.firstName} ${
        candidate.isLocked ? 'unlocked' : 'locked'
      }`,
      type: ToastType.Success
    });
  };

  const hasConversation = useMemo(
    () => candidates.some((candidate) => !!candidate.conversation),
    [candidates]
  );

  const openCandidateReportDrawer = useCallback(
    (candidateId: string, index: number, defaultCampaignTab: boolean) => {
      track(TrackingEventName.CandidateProfileDrawerOpen, { candidateId });
      updateSearchParams({
        searchParams: searchParams,
        newParam: {
          key: 'candidateId',
          value: candidateId
        },
        setSearchParams
      });

      openDrawer({
        drawerContentType: DrawerComponentType.CandidateReport,
        drawerComponentProps: {
          candidateId,
          candidateTableIndex: index,
          candidateIds: candidates.map((candidate) => candidate.id),
          defaultCampaignTab
        },
        onClose: () => {
          deleteSearchParams({
            searchParams: searchParams,
            key: 'candidateId',
            setSearchParams
          });
        }
      });
    },
    [candidates, searchParams, setSearchParams, openDrawer, track]
  );

  const openChangeStatusDrawer = useCallback(
    (candidateId: string) => {
      openDrawer({
        drawerContentType: DrawerComponentType.CandidateStatusDrawer,
        drawerComponentProps: {
          candidateIds: [candidateId]
        }
      });
    },
    [openDrawer]
  );

  useEffect(() => {
    if (drawerContentType !== DrawerComponentType.CandidateReport) return;
    updateDrawerComponentProps({
      candidateIds: candidates.map((candidate) => candidate.id)
    });
  }, [candidates, drawerContentType]);

  const openEditModal = useCallback(
    (candidateId: string) => {
      const candidate = candidates.find((c) => c.id === candidateId);
      if (!candidate) return;

      openDrawerV2({
        width: '479px',
        title: 'Edit candidate details',
        subtitle:
          'These changes will not update the candidate’s record in your ATS (if connected).',
        children: <EditCandidateDetails candidate={candidate} />
      });
    },
    [candidates, openDrawerV2]
  );

  const mappedCandidates = mapCandidatesDataToTableColumns(candidates);

  const columns = candidatesTableColumns({
    hasCampaign: hasConversation,
    onOpenCandidateDrawer: openCandidateReportDrawer,
    openChangeStatusDrawer,
    onLockStatus: updateCandidateLockStatus,
    openEditModal,
    isReadOnly,
    isAtsApplicationSource: !!integration,
    integration
  });

  const onClearSelectedCandidates = useCallback(() => {
    tableRef.current?.select([]);
    closeBottomDrawer();
  }, [closeBottomDrawer]);

  const handleSelectionChanged = useCallback(
    (selectedIds: string[]) => {
      if (selectedIds.length > 0) {
        if (isOpen) {
          updateBottomDrawerComponentProps({
            candidateIdsSelected: selectedIds,
            candidatesList: candidates,
            onClearSelectedCandidates
          });
        } else {
          openBottomDrawer({
            drawerContentType: BottomDrawerComponentType.AnalyseActions,
            drawerComponentProps: {
              candidateIdsSelected: selectedIds,
              candidatesList: candidates,
              onClearSelectedCandidates
            },
            onClose: onClearSelectedCandidates
          });
        }
      } else {
        closeBottomDrawer();
      }
    },
    [
      isOpen,
      candidates,
      onClearSelectedCandidates,
      updateBottomDrawerComponentProps,
      openBottomDrawer,
      closeBottomDrawer
    ]
  );

  return (
    <TableV3
      ref={tableRef}
      tableId="candidates-table"
      columns={columns}
      data={mappedCandidates}
      onFiltersChanged={onFiltersChanged}
      defaultSort={sort}
      onSort={onSort}
      loading={isLoading}
      emptyStateComponent={emptyStateComponent}
      selectionOptions={
        !isReadOnly
          ? {
              mode: 'multiRow',
              identifier: 'id',
              onSelectionChanged: handleSelectionChanged
            }
          : undefined
      }
      paginationOptions={{
        pageSize: 50,
        totalRows: paginationTotalRows,
        currentPage,
        onChangePage,
        onChangePageSize
      }}
    />
  );
};
